/* eslint-disable @typescript-eslint/no-namespace */
import { max } from 'd3-array';
import moment from 'moment';
import {
  DealVersion,
  Maturity,
  BasicMaturity,
  SecurityType,
  Series,
  BasicSeries,
  PriceType,
  TreasuryFeed
} from '../../types/deal.types';
import { STRUCTURE_GRAPH_MIN_PAR } from '../../utils/converters/value-providers';

enum ClosedOrNotOfferedMaturityCriteria {
  noRetail = 'No Retail',
  isNotReoffered = 'NRO',
  isSealedBid = 'Sealed Bid',
  isSoldNotAvailable = 'Sold Not Offered'
}

export enum OptionsType {
  Blank = '(Blank)'
}

export namespace DealMaturities {
  export interface ParTotalCount {
    total: number;
    count: number;
  }

  export namespace Structure {
    export function getAllShared(deal: DealVersion) {
      return deal.maturities.filter(isShared);
    }

    export function isAllShared(deal: DealVersion) {
      return deal.maturities.every(isShared);
    }

    export function isShared(mat: Maturity | BasicMaturity): boolean {
      if (typeof mat.numberBonds === 'number' && mat.numberBonds > 0) {
        return true;
      }
      if (
        mat.securityType === SecurityType.zero &&
        typeof mat.presentValue === 'number' &&
        mat.presentValue > 0
      ) {
        return true;
      }
      return false;
    }
  }

  export function getMaxPar(deal: DealVersion): number {
    const matSums: {
      [key: number]: number;
    } = DealMaturities.Structure.getAllShared(deal).reduce(
      (acc, maturity: Maturity) => {
        const year: number = moment(maturity.maturityDate).year();
        acc[year] = (acc[year] || 0) + maturity.numberBonds;
        return acc;
      },
      {}
    );

    return (
      max(Object.keys(matSums).map((key) => matSums[key])) ||
      STRUCTURE_GRAPH_MIN_PAR
    );
  }

  export function isClosedOrNotOfferedMaturity(mat: Maturity): boolean {
    return Object.keys(ClosedOrNotOfferedMaturityCriteria).some(
      (key) => mat[key]
    );
  }

  export function getClosedOrNotOfferedMaturityInfo(mat: Maturity): string {
    return Object.keys(ClosedOrNotOfferedMaturityCriteria)
      .filter((key) => mat[key])
      .map((key) => ClosedOrNotOfferedMaturityCriteria[key])
      .join(', ');
  }

  export function getGroupBySeries<T extends Maturity | BasicMaturity>(
    series: Series | BasicSeries,
    maturities: T[]
  ): T[] {
    return maturities.filter((m) => series.id === m.seriesId);
  }

  export function getUniqueSecTypesForMaturitiesBySeries(
    series: Series | BasicSeries,
    maturities: (Maturity | BasicMaturity)[]
  ): SecurityType[] {
    return getGroupBySeries(series, maturities)
      .map((m) => m.securityType)
      .filter((v, i, arr) => {
        return arr.indexOf(v) === i;
      });
  }

  export function getGroupBySeriesAndSecType<
    T extends Maturity | BasicMaturity
  >(series: Series, securityType: SecurityType, maturities: T[]): T[] {
    return getGroupBySeries(series, maturities).filter(
      (m) => m.securityType === securityType
    );
  }

  export function priceFilterPass(
    priceType: PriceType,
    maturity: BasicMaturity
  ): boolean {
    switch (priceType) {
      case PriceType.discount: {
        return maturity.price && maturity.price < 100;
      }
      case PriceType.par: {
        return maturity.price && maturity.price === 100;
      }
      case PriceType.premium: {
        return maturity.price && maturity.price > 100;
      }
      default: {
        return (
          priceType &&
          priceType === OptionsType.Blank &&
          (maturity.price === null || maturity.price === undefined)
        );
      }
    }
  }

  export function getTreasuryYear(maturity: BasicMaturity): TreasuryFeed {
    const currentYear = moment().year();
    const yearFromMaturity =
      maturity.taxableTsyMat &&
      getYearFromTaxableTsyYld(maturity.taxableTsyMat);

    if (!yearFromMaturity || yearFromMaturity < currentYear) {
      return null;
    }

    return DealMaturities.mapToTreasuryYear(yearFromMaturity, currentYear);
  }

  export function mapToTreasuryYear(
    year: number,
    startFromYear: number
  ): TreasuryFeed {
    const yearsDiff = year - startFromYear;

    if (yearsDiff > 30) {
      return TreasuryFeed.thirtyYears;
    }
    const treasuryYearTable = getTreasuryYearTable();
    return treasuryYearTable[yearsDiff];
  }

  export function getTreasuryYearTable(): {
    [yearsDiff: number]: TreasuryFeed;
  } {
    return {
      0: TreasuryFeed.oneYear,
      1: TreasuryFeed.oneYear,
      2: TreasuryFeed.twoYears,
      3: TreasuryFeed.threeYears,
      4: TreasuryFeed.fiveYears,
      5: TreasuryFeed.fiveYears,
      6: TreasuryFeed.sevenYears,
      7: TreasuryFeed.sevenYears,
      8: TreasuryFeed.tenYears,
      9: TreasuryFeed.tenYears,
      10: TreasuryFeed.tenYears,
      11: TreasuryFeed.tenYears,
      12: TreasuryFeed.tenYears,
      13: TreasuryFeed.tenYears,
      14: TreasuryFeed.tenYears,
      15: TreasuryFeed.tenYears,
      16: TreasuryFeed.thirtyYears,
      17: TreasuryFeed.thirtyYears,
      18: TreasuryFeed.thirtyYears,
      19: TreasuryFeed.thirtyYears,
      20: TreasuryFeed.thirtyYears,
      21: TreasuryFeed.thirtyYears,
      22: TreasuryFeed.thirtyYears,
      23: TreasuryFeed.thirtyYears,
      24: TreasuryFeed.thirtyYears,
      25: TreasuryFeed.thirtyYears,
      26: TreasuryFeed.thirtyYears,
      27: TreasuryFeed.thirtyYears,
      28: TreasuryFeed.thirtyYears,
      29: TreasuryFeed.thirtyYears,
      30: TreasuryFeed.thirtyYears
    };
  }

  export function getYearFromTaxableTsyYld(value: string) {
    const parts = value.split('/');
    return +parts[parts.length - 1];
  }
}
